<script setup lang="ts">
import { Filter, useSearch, useSearchFilterStore } from '@ilteducation/content-search';
import { SortKey, SearchPanel, FilterDrawer, SearchFilters } from '@ilteducation/content-search';
import { computed, ComputedRef, ref, watch } from 'vue';
import { AvailabilityRealmsFilter, RoleFilter } from '@ilteducation/content-search/src/content-filters';
import { storeToRefs } from 'pinia';
import { useUserContext } from '@ilteducation/ilt-auth';
import useSubscriptions from '@/foundations/helpers/use-subscriptions';
import useMaterialFilterFields from '@/composables/use-materials-filter-fields';
import { useDisplay } from 'vuetify';
import { useLocale } from '@/plugins/translation';
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import { groupContentByOriginalTitle } from '@ilteducation/content';
import MaterialResults from '@/components/material/MaterialResults.vue';

const userContext = useUserContext();
const { roles, realm } = storeToRefs(userContext);
const subscriptions = useSubscriptions();

const { xs, smAndDown } = useDisplay();
const { t, l } = useLocale();

const searchStore = useSearch();
const { next } = searchStore;
const { result, isLoading, hasMore } = storeToRefs(searchStore);

const sortFields = ['lastAdded', 'titleDesc', 'titleAsc'] as SortKey[];
const { removeAllFilters } = useSearchFilterStore();
const limit = 100;
const materialFilterFields = useMaterialFilterFields();
const filterFields = computed(() => [...materialFilterFields.value]);
const isFiltersVisible = ref<boolean>(!smAndDown.value);

const filters: ComputedRef<Filter> = computed(() => ({
  ...RoleFilter(roles.value.map((role) => role.value).toString()),
  ...AvailabilityRealmsFilter(realm.value),
}));

const deduplicatedMaterials = ref<MaterialV1[]>([]);
watch(
  () => result.value,
  () => {
    deduplicatedMaterials.value = groupContentByOriginalTitle(result.value) as MaterialV1[];
  },
);
</script>

<template>
  <v-container fluid class="fill-vertical-space position-relative bg-surface-container pt-2">
    <v-col class="text-center pb-0">
      <h1 class="text-secondary mb-0">{{ t(l.material.title) }}</h1>
    </v-col>
    <v-container class="custom-container pt-0">
      <SearchPanel
        :sort-fields="sortFields"
        selected-sort-option="titleAsc"
        :filters="filters as Filter"
        :filter-fields="filterFields"
        :limit="limit"
        content-type="Material"
        :focused="false"
        filters-alias="materials"
        :estimated-nr-grid-cells="100"
        :search-subscription="subscriptions.map((subscription) => subscription.type)"
        @toggleFilter="isFiltersVisible = !isFiltersVisible"
      />
      <div
        class="search-content d-flex flex-grow-1 ga-6 ga-xl-8 ga-xxl-10"
        :class="{ 'flex-column-reverse': smAndDown, 'flex-grow-1': !xs }"
      >
        <div
          v-show="isFiltersVisible"
          class="search-filter order-2 v-col-12 v-col-md-4 v-col-lg-3 v-col-xxl-2 position-relative pa-0 flex-shrink-0 flex-grow-0"
        >
          <FilterDrawer v-if="smAndDown" :model-value="isFiltersVisible" @close="isFiltersVisible = false">
            <v-card-title class="d-flex justify-space-between align-center px-0">
              <v-btn
                class="rounded-lg text-none pa-1"
                size="large"
                color="primary"
                variant="text"
                :text="t(l.button_reset)"
                @click="removeAllFilters"
              />
              <v-spacer />
              <p class="text-center flex-1-0">{{ t(l.content_search.label_search_filters) }}</p>
              <v-spacer />
              <v-btn
                class="rounded-lg text-none pa-1"
                size="large"
                color="primary"
                variant="text"
                @click="isFiltersVisible = false"
              >
                {{ t(l.button_done) }}
              </v-btn>
            </v-card-title>
            <SearchFilters :current-subscription="subscriptions[0].type" />
          </FilterDrawer>
          <v-scroll-x-transition v-else>
            <SearchFilters :current-subscription="subscriptions[0].type" />
          </v-scroll-x-transition>
        </div>
        <v-row class="gc-10 gr-6 flex-grow-1">
          <v-container class="custom-container">
            <v-card class="px-6 rounded-xl">
              <v-card-text>
                <MaterialResults
                  :is-loading="isLoading"
                  :deduplicated-materials="deduplicatedMaterials"
                  :has-more="hasMore"
                  @load-more="next"
                ></MaterialResults>
              </v-card-text>
            </v-card>
          </v-container>
        </v-row>
      </div>
    </v-container>
  </v-container>
</template>

<style scoped lang="scss">
@import 'vuetify/settings';
:deep(thead) {
  display: none;
}
:deep(.v-img__error) {
  position: unset;
}
.custom-container {
  max-width: 78.125rem !important;
}
</style>
